import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import 'katex/dist/katex.min.css'
import { formatBotMessage } from './utils'
import { useScrollContext } from '../../common/contexts/scroll';
import { SCROLL_REFERENCE_KEYS } from '../../constants';
import CustomAnchor from '../custom-anchor';
import Spinner from '../spinner';
import HtmlTypewriter from './html-typewriter';
import { bomSelectedObject } from '../../atoms';

const ChatItem = ({ chatData, onRetry, chatType }) => {

    const [currentLoadingMessageIndex, setCurrentLoadingMessageIndex] = useState(0)

    const { scrollToElement } = useScrollContext()
    const waitingMessages = getWaitingMessages(chatType)
    const selectedObjectId = useRecoilValue(bomSelectedObject)
    const responseRef = useRef(null)

    useEffect(() => {
        if(chatData.status === "loading"){
            // when loading, scroll to Thinking text
            scrollToElement(SCROLL_REFERENCE_KEYS.chatList, `[data-chat-id="${chatData.id}"] .loading-response`)
        }else if(chatData.status === "streaming"){
            // when the first token arrives, scroll to the asked question so the
            // answer streams in below it (runs once - status stays "streaming")
            scrollToElement(SCROLL_REFERENCE_KEYS.chatList, `[data-chat-id="${chatData.id}"] .chat-question`, 125)
        }else if((chatData.status === "completed") || (chatData.status === "error")){
            // when completed or error, scroll to asked question
            scrollToElement(SCROLL_REFERENCE_KEYS.chatList, `[data-chat-id="${chatData.id}"] .chat-question`, 125)
        }
    }, [chatData.status, chatData.id, scrollToElement])

    const changeLoadingMessage = useCallback(() => {
        setCurrentLoadingMessageIndex(prevIndex => {
            // Stop at the last message
            if (prevIndex < waitingMessages.length - 1) {
                return prevIndex + 1;
            }
            return prevIndex;
        });
    }, [waitingMessages])

    // Cycle through waiting messages every 6 seconds during loading
    useEffect(() => {
        if (chatData.status === "loading") {
            const interval = setInterval(changeLoadingMessage, 6000);

            // this will remove the interval when status changes to anything other than loading
            return () => clearInterval(interval);
        } else {
            // Reset to first message when not loading
            setCurrentLoadingMessageIndex(0);
        }
    }, [chatData.status, changeLoadingMessage])

    // Highlight the currently selected object's reference link inside this response.
    // Done via direct DOM toggle (not by re-rendering the HTML) so HtmlTypewriter
    // doesn't restart whenever the selection changes.
    useEffect(() => {
        const root = responseRef.current;
        if (!root) return;
        const selectedKey = selectedObjectId != null ? String(selectedObjectId) : null;
        root.querySelectorAll('.ref-link-chat').forEach((span) => {
            const matches = selectedKey != null && span.dataset.objectid === selectedKey;
            span.classList.toggle('ref-link-selected', matches);
        });
    }, [selectedObjectId, chatData.response, chatData.status])

    return (
        <div className={`chat-item status-${chatData.status}`} data-chat-id={chatData.id}>
            <div className='chat-question'>
                <p className='chat-question-text' dir='auto'>{chatData.question}</p>
                {(chatData.status === "error") && <CustomAnchor className="retry-text" onClick={onRetry}>Try Again</CustomAnchor>}
            </div>
            <div className='chat-response' ref={responseRef}>
                {!!chatData.response && (
                    (chatData.status === "completed" && !chatData.streamed) ? (
                        // typewriter simulation, only for answers that arrived whole
                        <HtmlTypewriter htmlContent={formatBotMessage(chatData.response)} />
                    ) : (
                        <div dir='auto' dangerouslySetInnerHTML={{ __html: formatBotMessage(chatData.response) }}/>
                    )
                )}
                {/* thinking spinner with the AI steps — stays below the streamed content until the response is done */}
                {(chatData.status === "loading" || chatData.status === "streaming") && (
                    <div className='loading-response'>
                        <Spinner />
                        <p>
                            {/* stream progress messages (tool calls / thinking) take over from the canned ones once they arrive */}
                            <span className='typewriter-text' dir='auto' key={chatData.progressMessage || currentLoadingMessageIndex}>
                                {chatData.progressMessage || waitingMessages[currentLoadingMessageIndex]}
                            </span>
                            <br/>Stay here to keep it going.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

const getWaitingMessages = (chatType) => {
    const waitingMessages = [
        "Analyzing BIM data... ",
        "Analyzing documents data... ",
        "Matching data... ",
        "Parsing response...",
    ]

    if(chatType === "organization") {
        return waitingMessages.slice(1)
    }

    return waitingMessages
}


export default ChatItem